import type {
  Campaign,
  CampaignPatch,
  CreateCampaignRequest,
  GetCampaignsRequest,
} from '@/apis/generated/dialbunny';
import { CampaignFromJSON } from '@/apis/generated/dialbunny';
import { apiStoreFactory } from '../api-store-factory';
import type { StoreEntity } from '../api-store-factory';
import { STORE } from '@/store-ids';

export type CampaignStore = StoreEntity<
  Campaign,
  'id',
  GetCampaignsRequest,
  CreateCampaignRequest['campaignPostRequest'],
  CampaignPatch
>;

export const useCampaignStore = apiStoreFactory<
  CampaignStore['entity'],
  CampaignStore['id'],
  CampaignStore['filter'],
  CampaignStore['createInput'],
  CampaignStore['updateInput']
>(
  STORE.CAMPAIGN,
  { entityIdKey: 'id' },
  {
    create: async (campaignCreate, { dialbunnyApi }) => {
      return await dialbunnyApi.createCampaign({
        campaignPostRequest: campaignCreate,
      });
    },
    readAll: async (filter, { dialbunnyApi }, signal) => {
      return await dialbunnyApi.getCampaignsRaw(filter, { signal });
    },
    read: async (id, { dialbunnyApi }) => {
      return await dialbunnyApi.getCampaign({ campaignId: id });
    },
    update: async (campaignId, campaignPatch, { dialbunnyApi }) => {
      return await dialbunnyApi.updateCampaign({ campaignId, campaignPatch });
    },
    bulkRead: async (campaignIds, { dialbunnyApi }) => {
      const campaigns = await dialbunnyApi.getModelsByKeysOfKind({
        kind: 'Campaign',
        ids: campaignIds.join(','),
      });

      return campaigns.map(CampaignFromJSON);
    },
    discard: async (campaignId, { dialbunnyApi }) => {
      await dialbunnyApi.deleteCampaign({ campaignId });
    },
    duplicate: async (campaignId, { dialbunnyApi }) => {
      return await dialbunnyApi.duplicateCampaign({ campaignId });
    },
  },
);
